import { canUseDom } from "./canUseDom";

/*
 * Replacement for the legacy Backbone `tap .social-share` delegation
 * (src/client/core/views/mixins/social-sharing.js).
 *
 * Opens a centered popup, fetches /v4/link to append the canonical
 * UTM-tracking params to the share URL, then navigates the popup to
 * the brand's share template with the tracked URL + share text
 * substituted in. Behaviour matches the legacy handler so existing
 * analytics and share-URL formats continue to work unchanged.
 *
 */
export async function openSharePopup({
  urlTemplate,
  url,
  text,
  content,
  channel,
  width = 600,
  height = 320
}) {
  if (!canUseDom) return;

  // Step 1: open an empty popup synchronously. Doing the fetch first
  // would mean the popup is blocked by browsers (popups must open as a
  // direct result of a user gesture, not after an awaited fetch).
  const isMobile = wattpad?.utils?.getDeviceType?.() === "mobile";
  const popup = isMobile
    ? window.open("", "_blank")
    : (() => {
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        return window.open(
          "",
          channel,
          `toolbar=0,location=0,menubar=0,width=${width},height=${height},left=${left},top=${top}`
        );
      })();

  // Sever reverse-tabnabbing: with `noopener` we can't pass a target
  // attribute to `window.open`, so null the back-reference manually
  // once we have the window.
  if (popup) {
    popup.opener = null;
  }
  // If the popup was blocked, the navigation block at the bottom of
  // this function falls back to `window.location = finalUrl`.

  // Step 2: fetch the tracking-augmented URL from /v4/link.
  const params = new URLSearchParams({
    source: "web",
    fields: "url",
    content: content || "",
    medium: channel,
    uuid: wattpad?.utils?.getCookie?.("wp_id") || "",
    url: url || "",
    shorten: "1"
  });

  let trackedUrl = url;
  try {
    const response = await fetch(`/v4/link?${params}`, {
      credentials: "same-origin",
      headers: { Accept: "application/json" }
    });
    if (response.ok) {
      const data = await response.json();
      if (data && data.url) {
        trackedUrl = data.url;
      }
    }
  } catch {
    // /v4/link failed — fall back to the un-tracked URL.
  }

  // Step 3: substitute the tracked URL + share text into the brand's
  // URL template and navigate the popup (or current window if blocked).
  //
  // Important: use the global `window.sprintf` (sprintf-js) for the
  // text substitution, NOT `wattpad.utils.sprintf`. The latter has a
  // quirk: when the format string has no `%s` placeholder and an arg
  // is passed, it appends the first character of the arg to the end
  // of the string. The Twitter share text template is a plain string
  // with no `%s` (the URL goes in a separate URL-template slot), so
  // `wattpad.utils.sprintf(text, url)` would produce e.g.
  // `"...#wattpadh"` (the leading "h" of "https://..."). The legacy
  // Backbone share handler used `window.sprintf` for the same reason.
  // Required globals missing (sprintf-js / wattpad.utils) — close the
  // orphaned popup we opened above before bailing, so the user isn't
  // left staring at a blank window.
  if (!window.sprintf || !wattpad?.utils?.sprintf) {
    if (popup) {
      popup.close();
    }
    return;
  }
  const sharedText = text ? window.sprintf(text, trackedUrl) : trackedUrl;
  const finalUrl = wattpad.utils.sprintf(urlTemplate, [
    encodeURIComponent(sharedText),
    encodeURIComponent(trackedUrl)
  ]);

  // The fetch above can take long enough for the user to close the popup
  // (or for a popup-blocker fallback path to kick in). Guard with
  // `popup.closed` and wrap in try/catch so a navigation race doesn't
  // surface as an unhandled promise rejection at the call site (which
  // fires-and-forgets this function — see PartFooterActions.js).
  try {
    if (popup && !popup.closed) {
      popup.location = finalUrl;
    } else if (!popup) {
      window.location = finalUrl;
    }
    // popup existed but was closed mid-fetch — user dismissed; drop.
  } catch {
    // popup closed between the `popup.closed` check and the assignment.
  }
}
